/**
 * Generates a one-page AI Readiness PDF for a single consultant and downloads
 * it directly (no print dialog). Uses jsPDF + autotable client-side.
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as api from '../api/client';

interface ReportItem {
  name?: string;
  client?: string;
  issueLabel?: string;
  date?: string;
}

interface ReportFactor {
  key: string;
  label: string;
  ratio: number;
  numerator: number;
  denominator: number;
  items?: ReportItem[];
}

interface ReportScore {
  name?: string;
  overall: number;
  totalIssues: number;
  factors?: ReportFactor[];
  flaggedVacancies28Day?: ReportItem[];
}

function scoreRGB(ratio: number): [number, number, number] {
  if (ratio >= 1.0) return [63, 125, 79];   // green
  if (ratio >= 0.75) return [17, 17, 18];    // near-black
  return [176, 70, 31];                       // warning
}

/** Two-line plain-English summary of the consultant's issues. */
function buildSummary(score: ReportScore): [string, string] {
  const name = score.name || 'This consultant';
  const pct = Math.round(score.overall * 100);

  if (score.totalIssues === 0) {
    return [
      `${name} has an overall AI Readiness score of ${pct}% with no outstanding issues.`,
      `Data quality is in great shape — nothing to action right now.`,
    ];
  }

  const counts = (score.factors || [])
    .map(f => ({ label: f.label, count: f.items?.length || 0 }))
    .filter(f => f.count > 0);
  const flagged = (score.flaggedVacancies28Day || []).length;
  if (flagged) counts.push({ label: '28-day flagged vacancies', count: flagged });
  counts.sort((a, b) => b.count - a.count);

  const areas = counts.length;
  const top = counts.slice(0, 3).map(f => `${f.label} (${f.count})`).join(', ');

  return [
    `${name} has an overall AI Readiness score of ${pct}% with ${score.totalIssues} outstanding issue${score.totalIssues !== 1 ? 's' : ''} across ${areas} area${areas !== 1 ? 's' : ''}.`,
    `Biggest gaps: ${top}.`,
  ];
}

export function downloadScoreReport(score: ReportScore): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const M = 40;
  const name = score.name || 'Consultant';
  const pct = Math.round(score.overall * 100);
  const today = new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  const [line1, line2] = buildSummary(score);

  // Header
  doc.setFont('helvetica', 'bold').setFontSize(18).setTextColor(17, 17, 18);
  doc.text('SARAGOSSA', M, 48);
  doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(120, 120, 135);
  doc.text('AI Readiness Report', M, 63);
  doc.text(today, pageW - M, 48, { align: 'right' });
  doc.setDrawColor(17, 17, 18).setLineWidth(1).line(M, 74, pageW - M, 74);

  // Name + overall score
  doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(17, 17, 18);
  doc.text(name, M, 104);
  const [r, g, b] = scoreRGB(score.overall);
  doc.setFontSize(30).setTextColor(r, g, b);
  doc.text(`${pct}%`, M, 146);
  doc.setFont('helvetica', 'normal').setFontSize(11).setTextColor(100, 100, 110);
  doc.text(`${score.totalIssues} outstanding issue${score.totalIssues !== 1 ? 's' : ''}`, M + 78, 146);

  // Summary
  let y = 174;
  doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(120, 120, 135);
  doc.text('SUMMARY', M, y);
  y += 16;
  doc.setFont('helvetica', 'normal').setFontSize(11).setTextColor(40, 40, 45);
  const summaryLines = doc.splitTextToSize(`${line1}\n${line2}`, pageW - 2 * M);
  doc.text(summaryLines, M, y);
  y += summaryLines.length * 15 + 14;

  // Score breakdown table
  const factorBody = (score.factors || []).map(f => [
    f.label,
    `${Math.round(f.ratio * 100)}%`,
    `${f.numerator}/${f.denominator}`,
    String(f.items?.length || 0),
  ]);
  autoTable(doc, {
    startY: y,
    head: [['Factor', 'Score', 'Passing', 'Issues']],
    body: factorBody,
    theme: 'striped',
    headStyles: { fillColor: [17, 17, 18], textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 10 },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
    margin: { left: M, right: M },
  });

  // Issues table
  const allIssues: string[][] = [];
  for (const f of score.factors || []) {
    for (const it of f.items || []) {
      allIssues.push([it.name || '—', it.client || '—', f.label, it.issueLabel || '—', it.date ? new Date(it.date).toLocaleDateString() : '']);
    }
  }
  for (const it of score.flaggedVacancies28Day || []) {
    allIssues.push([it.name || '—', it.client || '—', '28-day flag', it.issueLabel || '—', it.date ? new Date(it.date).toLocaleDateString() : '']);
  }

  const afterFactors = (doc as any).lastAutoTable.finalY + 26;
  doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(17, 17, 18);
  doc.text(`Issues (${allIssues.length})`, M, afterFactors);
  autoTable(doc, {
    startY: afterFactors + 8,
    head: [['Name', 'Client', 'Area', 'Issue', 'Date']],
    body: allIssues.length ? allIssues : [['—', '—', '—', 'No outstanding issues', '']],
    theme: 'striped',
    headStyles: { fillColor: [17, 17, 18], textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    margin: { left: M, right: M },
  });

  const safe = name.replace(/[^\w-]+/g, '_');
  doc.save(`AI-Readiness-${safe}.pdf`);
}

/** Fetch a user's full score, then download their PDF report. */
export async function downloadReportForUser(userId: string): Promise<void> {
  const score = await api.getScore(userId);
  downloadScoreReport(score);
}
